import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Tag, Space, Popconfirm, message, Modal, Form, Input, Select, Row, Col, Divider, Steps,
  Radio,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, PauseCircleOutlined, EyeOutlined,
  FileTextOutlined, CheckCircleOutlined, BellOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';
import { roleService, Role } from '../../services/roleService';
import { userService, UserListItem } from '../../services/userService';

// 表单模板接口（从localStorage读取）
interface FormTemplate {
  id: string;
  name: string;
  key: string;
  fields: any[];
}

interface ProcessNode {
  id: string;
  name: string;
  type: 'start' | 'end' | 'approval' | 'cc';
  assigneeType?: 'user' | 'role' | 'creator' | 'previous' | 'all'; // all表示所有人可发起
  assignees?: string[]; // 后端使用assignees字段
  previousNodeId?: string; // 当assigneeType为previous时，指定哪个前置节点的审批人
  approvalType?: 'any' | 'all' | 'sequential'; // 后端使用any/all/sequential
  nextNodes?: string[]; // 后端使用nextNodes数组
  // 表单配置
  formType?: 'template' | 'route'; // 表单类型：模板或路由
  formTemplateId?: string; // 表单模板ID
  formRoute?: string; // 表单路由
}

const WorkflowDefinitions: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [definitions, setDefinitions] = useState<workflowService.WorkflowDefinition[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  const [defModalVisible, setDefModalVisible] = useState(false);
  const [editingDef, setEditingDef] = useState<workflowService.WorkflowDefinition | null>(null);
  const [processNodes, setProcessNodes] = useState<ProcessNode[]>([]);
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  
  // 角色和用户列表
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  
  const [defForm] = Form.useForm();

  useEffect(() => {
    loadData();
    loadFormTemplates();
    loadRoles();
    loadUsers();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.listDefinitions({ page, pageSize: 10 });
      if (res.data) {
        setDefinitions(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载角色列表
  const loadRoles = async () => {
    try {
      const res = await roleService.list({ page: 1, pageSize: 100 });
      if (res.list) {
        setRoles(res.list);
      }
    } catch {
      setRoles([]);
    }
  };

  // 加载用户列表
  const loadUsers = async () => {
    try {
      const res = await userService.list({ page: 1, perPage: 100 });
      if (res.users) {
        setUsers(res.users);
      }
    } catch {
      setUsers([]);
    }
  };

  // 加载表单模板（从localStorage）
  const loadFormTemplates = () => {
    try {
      const saved = localStorage.getItem('formTemplates');
      if (saved) {
        const templates = JSON.parse(saved);
        // 只加载已发布的模板
        setFormTemplates(templates.filter((t: any) => t.status === 1));
      }
    } catch {
      setFormTemplates([]);
    }
  };

  const handleCreate = () => {
    setEditingDef(null);
    defForm.resetFields();
    setProcessNodes([
      { id: 'start', name: '开始', type: 'start', nextNodes: ['approval1'], formType: 'template', assigneeType: 'all', assignees: [] },
      { id: 'approval1', name: '审批节点1', type: 'approval', assigneeType: 'user', approvalType: 'any', nextNodes: ['end'], formType: 'template' },
      { id: 'end', name: '结束', type: 'end' },
    ]);
    setDefModalVisible(true);
  };

  const handleEdit = (def: workflowService.WorkflowDefinition) => {
    setEditingDef(def);
    defForm.setFieldsValue(def);
    try {
      const content = def.content ? JSON.parse(def.content) : null;
      if (content?.nodes) setProcessNodes(content.nodes);
      else setProcessNodes([]);
    } catch {
      setProcessNodes([]);
    }
    setDefModalVisible(true);
  };

  const submitDef = async () => {
    try {
      const values = await defForm.validateFields();
      const content = JSON.stringify({ nodes: processNodes });
      const data = { ...values, content };
      
      if (editingDef) {
        await workflowService.updateDefinition(editingDef.id, data);
        message.success('更新成功');
      } else {
        await workflowService.createDefinition(data);
        message.success('创建成功');
      }
      setDefModalVisible(false);
      loadData();
    } catch {
      message.error('操作失败');
    }
  };

  const handlePublish = async (id: number) => {
    try {
      await workflowService.publishDefinition(id);
      message.success('发布成功');
      loadData();
    } catch {
      message.error('发布失败');
    }
  };

  const handleDisable = async (id: number) => {
    try {
      await workflowService.disableDefinition(id);
      message.success('停用成功');
      loadData();
    } catch {
      message.error('停用失败');
    }
  };

  const handleEnable = async (id: number) => {
    try {
      await workflowService.enableDefinition(id);
      message.success('启用成功');
      loadData();
    } catch {
      message.error('启用失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await workflowService.deleteDefinition(id);
      message.success('删除成功');
      loadData();
    } catch {
      message.error('删除失败');
    }
  };

  // 流程节点操作
  const addProcessNode = () => {
    const newId = `node_${Date.now()}`;
    const endNode = processNodes.find(n => n.type === 'end');
    const lastNode = [...processNodes].reverse().find(n => n.type === 'approval' || n.type === 'cc');
    const newNode: ProcessNode = { id: newId, name: '新审批节点', type: 'approval', assigneeType: 'user', approvalType: 'any', nextNodes: endNode ? [endNode.id] : [], formType: 'template' };
    const updatedNodes = processNodes.map(node => node.id === lastNode?.id ? { ...node, nextNodes: [newId] } : node);
    const endIndex = updatedNodes.findIndex(n => n.type === 'end');
    updatedNodes.splice(endIndex, 0, newNode);
    setProcessNodes(updatedNodes);
  };

  const updateProcessNode = (nodeId: string, updates: Partial<ProcessNode>) => {
    setProcessNodes(nodes => nodes.map(node => node.id === nodeId ? { ...node, ...updates } : node));
  };

  const removeProcessNode = (nodeId: string) => {
    const node = processNodes.find(n => n.id === nodeId);
    if (!node || node.type === 'start' || node.type === 'end') return;
    const prevNode = processNodes.find(n => n.nextNodes?.includes(nodeId));
    setProcessNodes(processNodes.filter(n => n.id !== nodeId).map(n => n.id === prevNode?.id ? { ...n, nextNodes: node.nextNodes } : n));
  };

  // 获取当前节点之前的所有审批节点（用于"上一审批人"选择）
  const getPreviousApprovalNodes = (currentNodeId: string): ProcessNode[] => {
    const currentIndex = processNodes.findIndex(n => n.id === currentNodeId);
    if (currentIndex <= 0) return [];
    // 返回当前节点之前的所有审批节点
    return processNodes.slice(0, currentIndex).filter(n => n.type === 'approval');
  };

  // 获取表单模板名称
  const getFormTemplateName = (templateId?: string) => {
    if (!templateId) return '未选择';
    const template = formTemplates.find(t => t.id === templateId);
    return template?.name || '未知模板';
  };

  const columns: ColumnsType<workflowService.WorkflowDefinition> = [
    { title: '流程标识', dataIndex: 'key', key: 'key', render: (key: string) => <Tag>{key}</Tag> },
    { title: '流程名称', dataIndex: 'name', key: 'name' },
    { title: '版本', dataIndex: 'version', key: 'version', render: (v: number) => <Tag color="blue">V{v}</Tag> },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (status: number) => {
        const info = workflowService.definitionStatusMap[status];
        return <Tag color={info?.color}>{info?.text}</Tag>;
      },
    },
    { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', render: (time: string) => time?.substring(0, 16) || '-' },
    {
      title: '操作', key: 'action', width: 320,
      render: (_, record) => (
        <Space>
          {/* 草稿和停用状态都可以编辑 */}
          {(record.status === 0 || record.status === 2) && (
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          )}
          {/* 草稿状态可以发布 */}
          {record.status === 0 && (
            <Popconfirm title="确定要发布此流程吗？" onConfirm={() => handlePublish(record.id)}>
              <Button size="small" type="primary" icon={<PlayCircleOutlined />}>发布</Button>
            </Popconfirm>
          )}
          {/* 已发布状态可以停用 */}
          {record.status === 1 && (
            <Popconfirm title="确定要停用此流程吗？" onConfirm={() => handleDisable(record.id)}>
              <Button size="small" danger icon={<PauseCircleOutlined />}>停用</Button>
            </Popconfirm>
          )}
          {/* 停用状态可以启用 */}
          {record.status === 2 && (
            <Popconfirm title="确定要启用此流程吗？" onConfirm={() => handleEnable(record.id)}>
              <Button size="small" type="primary" icon={<PlayCircleOutlined />}>启用</Button>
            </Popconfirm>
          )}
          {/* 已发布状态只能查看 */}
          {record.status === 1 && (
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleEdit(record)}>查看</Button>
          )}
          {/* 草稿和停用状态可以删除 */}
          {(record.status === 0 || record.status === 2) && (
            <Popconfirm title="确定要删除此流程吗？删除后不可恢复" onConfirm={() => handleDelete(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="流程定义" extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建流程</Button>}>
        <Table columns={columns} dataSource={definitions} rowKey="id" loading={loading}
          pagination={{ current: page, total, pageSize: 10, showTotal: (t) => `共 ${t} 条`, onChange: setPage }} />
      </Card>

      {/* 流程定义弹窗 */}
      <Modal title={editingDef ? '编辑流程' : '新建流程'} open={defModalVisible} onCancel={() => setDefModalVisible(false)} onOk={submitDef} width={900}>
        <Form form={defForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="key" label="流程标识" rules={[{ required: true }]}><Input placeholder="如: leave_approval" disabled={!!editingDef} /></Form.Item></Col>
            <Col span={12}><Form.Item name="name" label="流程名称" rules={[{ required: true }]}><Input placeholder="如: 请假审批" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}><Form.Item name="description" label="描述"><Input placeholder="流程描述" /></Form.Item></Col>
          </Row>
        </Form>
        <Divider>流程节点配置</Divider>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={addProcessNode}>添加审批节点</Button>
        </div>
        <Steps orientation="vertical" current={-1}
          items={processNodes.map((node) => ({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input value={node.name} onChange={(e) => updateProcessNode(node.id, { name: e.target.value })} style={{ width: 150 }} disabled={node.type === 'start' || node.type === 'end'} />
                {node.type !== 'start' && node.type !== 'end' && <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeProcessNode(node.id)} />}
              </div>
            ),
            description: node.type === 'end' ? null : node.type === 'start' ? (
              // 开始节点显示发起人配置和表单配置
              <div style={{ marginTop: 8, padding: 12, background: '#f0f9eb', borderRadius: 6 }}>
                {/* 发起人配置 */}
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: 13 }}>👤 发起人配置</div>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <span style={{ marginRight: 8 }}>发起人:</span>
                    <Select 
                      value={node.assigneeType || 'all'} 
                      onChange={(v) => updateProcessNode(node.id, { assigneeType: v, assignees: [] })} 
                      style={{ width: 120 }} 
                      size="small"
                    >
                      <Select.Option value="all">所有人</Select.Option>
                      <Select.Option value="user">指定用户</Select.Option>
                      <Select.Option value="role">指定角色</Select.Option>
                    </Select>
                  </Col>
                </Row>
                {(node.assigneeType === 'user' || node.assigneeType === 'role') && (
                  <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      {node.assigneeType === 'user' ? (
                        <Select
                          mode="multiple"
                          value={node.assignees}
                          onChange={(v) => updateProcessNode(node.id, { assignees: v })}
                          placeholder="选择可发起流程的用户"
                          style={{ width: '100%' }}
                          size="small"
                          showSearch
                          optionFilterProp="children"
                        >
                          {users.map(u => <Select.Option key={u.id} value={u.id}>{u.name || u.username}</Select.Option>)}
                        </Select>
                      ) : (
                        <Select
                          mode="multiple"
                          value={node.assignees}
                          onChange={(v) => updateProcessNode(node.id, { assignees: v })}
                          placeholder="选择可发起流程的角色"
                          style={{ width: '100%' }}
                          size="small"
                          showSearch
                          optionFilterProp="children"
                        >
                          {roles.map(r => <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>)}
                        </Select>
                      )}
                    </Col>
                  </Row>
                )}
                <Divider style={{ margin: '12px 0' }} dashed />
                <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>📋 发起表单配置</div>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Radio.Group 
                      value={node.formType || 'template'} 
                      onChange={(e) => updateProcessNode(node.id, { formType: e.target.value, formTemplateId: undefined, formRoute: undefined })}
                      size="small"
                    >
                      <Radio value="template">表单模板</Radio>
                      <Radio value="route">表单路由</Radio>
                    </Radio.Group>
                  </Col>
                  <Col span={16}>
                    {(node.formType || 'template') === 'template' ? (
                      <Select 
                        value={node.formTemplateId} 
                        onChange={(v) => updateProcessNode(node.id, { formTemplateId: v })} 
                        placeholder="选择发起表单模板" 
                        style={{ width: '100%' }} 
                        size="small"
                        allowClear
                      >
                        {formTemplates.map(t => <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>)}
                      </Select>
                    ) : (
                      <Input 
                        value={node.formRoute} 
                        onChange={(e) => updateProcessNode(node.id, { formRoute: e.target.value })} 
                        placeholder="输入表单页面路由，如: /forms/leave" 
                        size="small"
                      />
                    )}
                  </Col>
                </Row>
                {(node.formType || 'template') === 'template' && node.formTemplateId && (
                  <div style={{ marginTop: 8, color: '#52c41a', fontSize: 12 }}>
                    已选择: {getFormTemplateName(node.formTemplateId)}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 8, padding: 12, background: '#fafafa', borderRadius: 6 }}>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <span style={{ marginRight: 8 }}>节点类型:</span>
                    <Select value={node.type} onChange={(v) => updateProcessNode(node.id, { type: v as any })} style={{ width: 100 }} size="small">
                      <Select.Option value="approval">审批</Select.Option>
                      <Select.Option value="cc">抄送</Select.Option>
                    </Select>
                  </Col>
                  {node.type === 'approval' && (
                    <>
                      <Col span={8}>
                        <span style={{ marginRight: 8 }}>审批方式:</span>
                        <Select value={node.approvalType} onChange={(v) => updateProcessNode(node.id, { approvalType: v })} style={{ width: 100 }} size="small">
                          <Select.Option value="any">或签</Select.Option>
                          <Select.Option value="all">会签</Select.Option>
                          <Select.Option value="sequential">顺序</Select.Option>
                        </Select>
                      </Col>
                      <Col span={8}>
                        <span style={{ marginRight: 8 }}>审批人:</span>
                        <Select value={node.assigneeType} onChange={(v) => updateProcessNode(node.id, { assigneeType: v, assignees: [] })} style={{ width: 120 }} size="small">
                          <Select.Option value="user">指定用户</Select.Option>
                          <Select.Option value="role">指定角色</Select.Option>
                          <Select.Option value="creator">发起人</Select.Option>
                          <Select.Option value="previous">上一审批人</Select.Option>
                        </Select>
                      </Col>
                    </>
                  )}
                  {node.type === 'cc' && (
                    <Col span={8}>
                      <span style={{ marginRight: 8 }}>抄送人:</span>
                      <Select value={node.assigneeType || 'user'} onChange={(v) => updateProcessNode(node.id, { assigneeType: v, assignees: [] })} style={{ width: 120 }} size="small">
                        <Select.Option value="user">指定用户</Select.Option>
                        <Select.Option value="role">指定角色</Select.Option>
                        <Select.Option value="creator">发起人</Select.Option>
                      </Select>
                    </Col>
                  )}
                </Row>
                {/* 上一审批人节点选择 */}
                {node.type === 'approval' && node.assigneeType === 'previous' && (
                  <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Select
                        value={node.previousNodeId}
                        onChange={(v) => updateProcessNode(node.id, { previousNodeId: v })}
                        placeholder="选择引用哪个节点的审批人"
                        style={{ width: '100%' }}
                        size="small"
                      >
                        {getPreviousApprovalNodes(node.id).map(n => (
                          <Select.Option key={n.id} value={n.id}>{n.name}</Select.Option>
                        ))}
                      </Select>
                      {getPreviousApprovalNodes(node.id).length === 0 && (
                        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>当前节点之前没有审批节点</div>
                      )}
                    </Col>
                  </Row>
                )}
                {/* 审批人/抄送人选择 */}
                {(node.type === 'approval' || node.type === 'cc') && (node.assigneeType === 'user' || node.assigneeType === 'role') && (
                  <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      {node.assigneeType === 'user' ? (
                        <Select
                          mode="multiple"
                          value={node.assignees}
                          onChange={(v) => updateProcessNode(node.id, { assignees: v })}
                          placeholder="选择审批用户"
                          style={{ width: '100%' }}
                          size="small"
                          showSearch
                          optionFilterProp="children"
                        >
                          {users.map(u => <Select.Option key={u.id} value={u.id}>{u.name || u.username}</Select.Option>)}
                        </Select>
                      ) : (
                        <Select
                          mode="multiple"
                          value={node.assignees}
                          onChange={(v) => updateProcessNode(node.id, { assignees: v })}
                          placeholder="选择审批角色"
                          style={{ width: '100%' }}
                          size="small"
                          showSearch
                          optionFilterProp="children"
                        >
                          {roles.map(r => <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>)}
                        </Select>
                      )}
                    </Col>
                  </Row>
                )}
                {/* 表单配置 */}
                <Divider style={{ margin: '12px 0' }} dashed />
                <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>📋 表单配置</div>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Radio.Group 
                      value={node.formType || 'template'} 
                      onChange={(e) => updateProcessNode(node.id, { formType: e.target.value, formTemplateId: undefined, formRoute: undefined })}
                      size="small"
                    >
                      <Radio value="template">表单模板</Radio>
                      <Radio value="route">表单路由</Radio>
                    </Radio.Group>
                  </Col>
                  <Col span={16}>
                    {(node.formType || 'template') === 'template' ? (
                      <Select 
                        value={node.formTemplateId} 
                        onChange={(v) => updateProcessNode(node.id, { formTemplateId: v })} 
                        placeholder="选择表单模板" 
                        style={{ width: '100%' }} 
                        size="small"
                        allowClear
                      >
                        {formTemplates.map(t => <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>)}
                      </Select>
                    ) : (
                      <Input 
                        value={node.formRoute} 
                        onChange={(e) => updateProcessNode(node.id, { formRoute: e.target.value })} 
                        placeholder="输入表单页面路由，如: /forms/leave" 
                        size="small"
                      />
                    )}
                  </Col>
                </Row>
                {(node.formType || 'template') === 'template' && node.formTemplateId && (
                  <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                    已选择: {getFormTemplateName(node.formTemplateId)}
                  </div>
                )}
              </div>
            ),
            icon: node.type === 'start' ? <PlayCircleOutlined style={{ color: '#52c41a' }} /> : node.type === 'end' ? <CheckCircleOutlined style={{ color: '#1890ff' }} /> : node.type === 'cc' ? <BellOutlined style={{ color: '#faad14' }} /> : <FileTextOutlined style={{ color: '#1890ff' }} />,
          }))}
        />
      </Modal>

    </div>
  );
};

export default WorkflowDefinitions;
